/**
 * @author  Agence Dn'D <contact@dnd.fr>
 * @license https://opensource.org/licenses/osl-3.0.php  Open Software License (OSL 3.0)
 * @link    https://www.dnd.fr/
 */

(() => {
    'use strict';

    const supportsAnimationTimeline = () => {
        const supports = CSS.supports('animation-timeline', 'view()');
        return supports;
    };

    if (supportsAnimationTimeline()) {
        return;
    }

    const isMobile = () => {
        return window.innerWidth <= 767.98;
    };

    class ColorUtils {
        static lerp(start, end, progress) {
            return start + (end - start) * progress;
        }

        static parseColor(colorVar) {
            if (colorVar.startsWith('rgb')) {
                return colorVar;
            }

            if (colorVar.startsWith('#')) {
                const hex = colorVar.replace('#', '');
                const r = parseInt(hex.substr(0, 2), 16);
                const g = parseInt(hex.substr(2, 2), 16);
                const b = parseInt(hex.substr(4, 2), 16);
                return `rgb(${r}, ${g}, ${b})`;
            }

            const temp = document.createElement('div');
            temp.style.color = colorVar;
            document.body.appendChild(temp);
            const computed = getComputedStyle(temp).color;
            document.body.removeChild(temp);
            return computed;
        }

        static interpolateColor(color1, color2, progress) {
            const rgb1 = color1.match(/\d+/g).map(Number);
            const rgb2 = color2.match(/\d+/g).map(Number);

            const r = Math.round(ColorUtils.lerp(rgb1[0], rgb2[0], progress));
            const g = Math.round(ColorUtils.lerp(rgb1[1], rgb2[1], progress));
            const b = Math.round(ColorUtils.lerp(rgb1[2], rgb2[2], progress));

            return `rgb(${r}, ${g}, ${b})`;
        }

        static calculateProgress(scrollProgress, keyframes) {
            for (let i = 0; i < keyframes.length - 1; i++) {
                const current = keyframes[i];
                const next = keyframes[i + 1];

                if (scrollProgress >= current.offset && scrollProgress <= next.offset) {
                    const segmentProgress = (scrollProgress - current.offset) / (next.offset - current.offset);
                    return {
                        from: current,
                        to: next,
                        progress: segmentProgress
                    };
                }
            }

            return {
                from: keyframes[keyframes.length - 1],
                to: keyframes[keyframes.length - 1],
                progress: 1
            };
        }
    }

    class ScrollProgressCalculator {
        static getScrollProgress(element) {
            const rect = element.getBoundingClientRect();
            const viewportHeight = window.innerHeight;

            const entryPoint = viewportHeight;
            const exitPoint = 0 - rect.height;

            const currentPosition = rect.top;

            const totalDistance = entryPoint - exitPoint;

            const distanceTraveled = entryPoint - currentPosition;

            let progress = distanceTraveled / totalDistance;

            progress = Math.max(0, Math.min(1, progress));

            return progress;
        }
    }

    class ScrollAnimationHandler {
        constructor(element, animationFn, shouldAnimate = () => true) {
            this.element = element;
            this.animationFn = animationFn;
            this.shouldAnimate = shouldAnimate;
            this.isActive = false;
            this.rafId = null;

            this.observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        this.start();
                    } else {
                        this.stop();
                    }
                });
            }, {
                rootMargin: '200px'
            });

            this.observer.observe(this.element);
        }

        start() {
            if (this.isActive) return;
            if (!this.shouldAnimate()) return;
            this.isActive = true;
            this.update();
        }

        stop() {
            this.isActive = false;
            if (this.rafId) {
                cancelAnimationFrame(this.rafId);
                this.rafId = null;
            }
        }

        update() {
            if (!this.isActive) return;
            if (!this.shouldAnimate()) {
                this.stop();
                return;
            }

            const progress = ScrollProgressCalculator.getScrollProgress(this.element);
            this.animationFn(this.element, progress);

            this.rafId = requestAnimationFrame(() => this.update());
        }

        destroy() {
            this.stop();
            if (this.observer) {
                this.observer.disconnect();
            }
        }
    }

    class ScrollAnimationFallback {
        constructor() {
            const style = getComputedStyle(document.documentElement);
            this.colors = {
                primary: style.getPropertyValue('--primary').trim(),
                egg: style.getPropertyValue('--egg').trim(),
                secondary500: style.getPropertyValue('--secondary-500').trim(),
                deepGrey: style.getPropertyValue('--deep-grey').trim()
            };

            this.colorsRgb = {
                primary: ColorUtils.parseColor(this.colors.primary),
                egg: ColorUtils.parseColor(this.colors.egg),
                secondary500: ColorUtils.parseColor(this.colors.secondary500),
                deepGrey: ColorUtils.parseColor(this.colors.deepGrey)
            };

            this.handlers = [];
            this.mutationObserver = null;
        }

        animateBackgroundColor(element, scrollProgress) {
            const keyframes = [
                { offset: 0, color: this.colorsRgb.primary },
                { offset: 0.18, color: this.colorsRgb.primary },
                { offset: 0.25, color: this.colorsRgb.egg },
                { offset: 0.43, color: this.colorsRgb.egg },
                { offset: 0.50, color: this.colorsRgb.secondary500 },
                { offset: 1, color: this.colorsRgb.secondary500 }
            ];

            const state = ColorUtils.calculateProgress(scrollProgress, keyframes);

            let finalColor;
            if (state.from.color === state.to.color) {
                finalColor = state.from.color;
            } else {
                finalColor = ColorUtils.interpolateColor(state.from.color, state.to.color, state.progress);
            }

            element.style.setProperty('background-color', finalColor, 'important');
        }

        animateTextColor(element, scrollProgress) {
            const keyframes = [
                { offset: 0, color: this.colorsRgb.deepGrey },
                { offset: 0.25, color: this.colorsRgb.primary },
                { offset: 1, color: this.colorsRgb.primary }
            ];

            const state = ColorUtils.calculateProgress(scrollProgress, keyframes);

            let finalColor;
            if (state.from.color === state.to.color) {
                finalColor = state.from.color;
            } else {
                finalColor = ColorUtils.interpolateColor(state.from.color, state.to.color, state.progress);
            }

            element.style.setProperty('color', finalColor, 'important');
            if (element.tagName.toLowerCase() === 'path') {
                element.style.setProperty('fill', finalColor, 'important');
            }
        }

        animateRadianceColor(element, scrollProgress) {
            const keyframes = [
                { offset: 0, color: this.colorsRgb.primary },
                { offset: 0.18, color: this.colorsRgb.primary },
                { offset: 0.25, color: this.colorsRgb.deepGrey },
                { offset: 0.43, color: this.colorsRgb.deepGrey },
                { offset: 0.50, color: this.colorsRgb.primary },
                { offset: 1, color: this.colorsRgb.primary }
            ];

            const state = ColorUtils.calculateProgress(scrollProgress, keyframes);

            let finalColor;
            if (state.from.color === state.to.color) {
                finalColor = state.from.color;
            } else {
                finalColor = ColorUtils.interpolateColor(state.from.color, state.to.color, state.progress);
            }

            element.style.setProperty('fill', finalColor, 'important');
            element.style.setProperty('stroke', finalColor, 'important');
            element.style.setProperty('color', finalColor, 'important');
        }

        animateDotsColor(element, scrollProgress) {
            const keyframes = [
                { offset: 0, color: this.colorsRgb.deepGrey },
                { offset: 0.25, color: this.colorsRgb.primary },
                { offset: 1, color: this.colorsRgb.primary }
            ];

            const state = ColorUtils.calculateProgress(scrollProgress, keyframes);

            let finalColor;
            if (state.from.color === state.to.color) {
                finalColor = state.from.color;
            } else {
                finalColor = ColorUtils.interpolateColor(state.from.color, state.to.color, state.progress);
            }

            element.style.setProperty('background-color', finalColor, 'important');
        }

        animateDotsActiveColor(element, scrollProgress) {
            const keyframes = [
                { offset: 0, color: this.colorsRgb.deepGrey, border: 'transparent' },
                { offset: 0.25, color: this.colorsRgb.primary, border: this.colorsRgb.primary },
                { offset: 1, color: this.colorsRgb.primary, border: this.colorsRgb.primary }
            ];

            const state = ColorUtils.calculateProgress(scrollProgress, keyframes);

            let finalColor;
            if (state.from.color === state.to.color) {
                finalColor = state.from.color;
            } else {
                finalColor = ColorUtils.interpolateColor(state.from.color, state.to.color, state.progress);
            }

            element.style.setProperty('background-color', finalColor, 'important');

            if (scrollProgress < 0.25) {
                element.style.setProperty('border', '1px solid transparent', 'important');
            } else {
                element.style.setProperty('border', `1px solid ${this.colorsRgb.primary}`, 'important');
            }
        }

        animateShopTheLookBackground(element, scrollProgress) {
            const keyframes = [
                { offset: 0, color: this.colorsRgb.secondary500 },
                { offset: 0.18, color: this.colorsRgb.secondary500 },
                { offset: 0.25, color: this.colorsRgb.primary },
                { offset: 0.43, color: this.colorsRgb.primary },
                { offset: 0.50, color: this.colorsRgb.secondary500 },
                { offset: 1, color: this.colorsRgb.secondary500 }
            ];

            const state = ColorUtils.calculateProgress(scrollProgress, keyframes);

            let finalColor;
            if (state.from.color === state.to.color) {
                finalColor = state.from.color;
            } else {
                finalColor = ColorUtils.interpolateColor(state.from.color, state.to.color, state.progress);
            }

            element.style.setProperty('background-color', finalColor, 'important');
        }

        animateShopTheLookRadianceStroke(element, scrollProgress) {
            const keyframes = [
                { offset: 0, color: this.colorsRgb.primary },
                { offset: 0.18, color: this.colorsRgb.primary },
                { offset: 0.25, color: this.colorsRgb.deepGrey },
                { offset: 0.43, color: this.colorsRgb.deepGrey },
                { offset: 0.50, color: this.colorsRgb.primary },
                { offset: 1, color: this.colorsRgb.primary }
            ];

            const state = ColorUtils.calculateProgress(scrollProgress, keyframes);

            let finalColor;
            if (state.from.color === state.to.color) {
                finalColor = state.from.color;
            } else {
                finalColor = ColorUtils.interpolateColor(state.from.color, state.to.color, state.progress);
            }

            element.style.setProperty('stroke', finalColor, 'important');
        }

        initPushCollectionCards() {
            const section = document.querySelector('.section-push-collection-cards.sgenshop-custom');

            if (!section) {
                return;
            }

            this.handlers.push(
                new ScrollAnimationHandler(
                    section,
                    (el, progress) => this.animateBackgroundColor(el, progress)
                )
            );

            const quotationTexts = section.querySelectorAll('.quotation-container p');
            quotationTexts.forEach((el) => {
                this.handlers.push(
                    new ScrollAnimationHandler(
                        el,
                        (el, progress) => this.animateTextColor(el, progress)
                    )
                );
            });

            const quotationSvgPaths = section.querySelectorAll('.quotation-container svg path');
            quotationSvgPaths.forEach((el) => {
                this.handlers.push(
                    new ScrollAnimationHandler(
                        el,
                        (el, progress) => this.animateTextColor(el, progress)
                    )
                );
            });

            const homeTitle = section.querySelectorAll('.hometitle');
            homeTitle.forEach((el) => {
                this.handlers.push(
                    new ScrollAnimationHandler(
                        el,
                        (el, progress) => this.animateTextColor(el, progress)
                    )
                );
            });

            const textColumnText = section.querySelectorAll('.text-column__text');
            textColumnText.forEach((el) => {
                this.handlers.push(
                    new ScrollAnimationHandler(
                        el,
                        (el, progress) => this.animateTextColor(el, progress)
                    )
                );
            });

            const radiancePaths = section.querySelectorAll('.radiance-background svg path');
            radiancePaths.forEach((el) => {
                this.handlers.push(
                    new ScrollAnimationHandler(
                        el,
                        (el, progress) => this.animateRadianceColor(el, progress)
                    )
                );
            });

            const dots = section.querySelectorAll('.glide__bullet:not(.glide__bullet--active)');
            dots.forEach((el) => {
                this.handlers.push(
                    new ScrollAnimationHandler(
                        el,
                        (el, progress) => this.animateDotsColor(el, progress)
                    )
                );
            });

            const observeDotActiveChanges = () => {
                const activeDots = section.querySelectorAll('.glide__bullet--active');
                activeDots.forEach((el) => {
                    const exists = this.handlers.find(h => h.element === el);
                    if (!exists) {
                        this.handlers.push(
                            new ScrollAnimationHandler(
                                el,
                                (el, progress) => this.animateDotsActiveColor(el, progress)
                            )
                        );
                    }
                });
            };

            const bulletsContainer = section.querySelector('.glide__bullets');
            if (bulletsContainer) {
                this.mutationObserver = new MutationObserver(observeDotActiveChanges);
                this.mutationObserver.observe(bulletsContainer, {
                    attributes: true,
                    attributeFilter: ['class'],
                    subtree: true
                });
            }

            observeDotActiveChanges();
        }

        initShopTheLook() {
            const section = document.querySelector('.section-shop-the-look.sgenshop-custom');

            if (!section) {
                return;
            }

            this.handlers.push(
                new ScrollAnimationHandler(
                    section,
                    (el, progress) => this.animateShopTheLookBackground(el, progress),
                    isMobile
                )
            );

            const radiancePaths = section.querySelectorAll('.radiance-background svg path');
            radiancePaths.forEach((el) => {
                this.handlers.push(
                    new ScrollAnimationHandler(
                        el,
                        (el, progress) => this.animateShopTheLookRadianceStroke(el, progress),
                        isMobile
                    )
                );
            });
        }

        init() {
            this.initPushCollectionCards();
            this.initShopTheLook();

            let resizeTimeout;
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    this.handlers.forEach(handler => {
                        if (handler.shouldAnimate !== (() => true)) {
                            if (handler.isActive && !handler.shouldAnimate()) {
                                handler.stop();
                            } else if (!handler.isActive && handler.shouldAnimate()) {
                                const rect = handler.element.getBoundingClientRect();
                                const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
                                if (isInViewport) {
                                    handler.start();
                                }
                            }
                        }
                    });
                }, 150);
            });

            window.addEventListener('beforeunload', () => {
                this.handlers.forEach(handler => handler.destroy());
                if (this.mutationObserver) {
                    this.mutationObserver.disconnect();
                }
            });
        }
    }

    const initScrollAnimations = () => {
        const scrollAnimation = new ScrollAnimationFallback();
        scrollAnimation.init();
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initScrollAnimations);
    } else {
        initScrollAnimations();
    }
})();